/**
 * WebAuthn / platform authenticator helpers for PWA Face ID unlock.
 * Uses the native Web Authentication API (no third-party WebAuthn client).
 */

const PRF_SALT = new TextEncoder().encode('balance.vault.prf.v1');

export type AuthenticatorLabel = 'Face ID' | 'Touch ID' | 'Windows Hello' | 'Biometric' | 'Passkey';

export function getAuthenticatorLabel(): AuthenticatorLabel {
  if (typeof navigator === 'undefined') return 'Passkey';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'Face ID';
  if (/Mac/i.test(ua) && navigator.maxTouchPoints === 0) return 'Touch ID';
  if (/Windows/i.test(ua)) return 'Windows Hello';
  if (/Android/i.test(ua)) return 'Biometric';
  return 'Passkey';
}

export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) return false;
  try {
    if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Best-effort PRF support probe. Prefer true only when the browser advertises
 * the extension or a prior enroll recorded prfEnabled.
 */
export async function supportsPrfExtension(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) return false;
  try {
    const caps = (
      PublicKeyCredential as unknown as {
        getClientCapabilities?: () => Promise<Record<string, boolean>>;
      }
    ).getClientCapabilities;
    if (typeof caps === 'function') {
      const result = await caps.call(PublicKeyCredential);
      const extensionPrf = result?.['extension:prf'];
      if (typeof extensionPrf === 'boolean') return extensionPrf;
      if (typeof result?.prf === 'boolean') return result.prf;
    }
  } catch {
    /* ignore */
  }
  // Safari / iOS often omit capability maps; PRF may still work at assert time.
  // Callers should treat enroll-time prf results as authoritative.
  return false;
}

export function bufferToBase64Url(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export function base64UrlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const binary = atob(padded + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function decodeCredentialCreationOptions(
  options: PublicKeyCredentialCreationOptionsJSON
): PublicKeyCredentialCreationOptions {
  const decoded = {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64UrlToBuffer(options.user.id),
    },
    excludeCredentials: options.excludeCredentials?.map((c) => ({
      ...c,
      id: base64UrlToBuffer(c.id),
      type: c.type ?? 'public-key',
      transports: (c.transports as AuthenticatorTransport[] | undefined) ?? ['internal'],
    })),
    // Always prefer platform biometric (Face ID / Touch ID), not cross-device passkeys.
    authenticatorSelection: {
      authenticatorAttachment: 'platform' as AuthenticatorAttachment,
      residentKey: 'discouraged' as ResidentKeyRequirement,
      requireResidentKey: false,
      userVerification: 'required' as UserVerificationRequirement,
    },
  } as PublicKeyCredentialCreationOptions;
  // hints is WebAuthn L3; not in all TS DOM libs yet
  (decoded as PublicKeyCredentialCreationOptions & { hints?: string[] }).hints =
    options.hints ?? ['client-device'];
  return decoded;
}

function ensureInternalTransports(
  allowCredentials: PublicKeyCredentialRequestOptions['allowCredentials']
): PublicKeyCredentialDescriptor[] | undefined {
  if (!allowCredentials?.length) return allowCredentials;
  return allowCredentials.map((c) => {
    const transports = new Set<AuthenticatorTransport>(c.transports ?? []);
    transports.add('internal');
    return {
      ...c,
      type: c.type ?? 'public-key',
      transports: [...transports],
    };
  });
}

function decodeCredentialRequestOptions(
  options: PublicKeyCredentialRequestOptionsJSON
): PublicKeyCredentialRequestOptions {
  const allowCredentials = options.allowCredentials?.map((c) => ({
    ...c,
    id: base64UrlToBuffer(c.id),
    type: (c.type ?? 'public-key') as PublicKeyCredentialType,
    transports: (c.transports as AuthenticatorTransport[] | undefined) ?? ['internal'],
  }));

  const decoded = {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    allowCredentials: ensureInternalTransports(allowCredentials),
    userVerification: options.userVerification ?? 'required',
  } as PublicKeyCredentialRequestOptions & { hints?: string[] };
  decoded.hints = options.hints ?? ['client-device'];
  return decoded;
}

export interface PublicKeyCredentialCreationOptionsJSON {
  challenge: string;
  rp: { name: string; id?: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  excludeCredentials?: Array<{
    id: string;
    type?: PublicKeyCredentialType;
    transports?: string[];
  }>;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
  extensions?: AuthenticationExtensionsClientInputs;
  hints?: string[];
}

export interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{
    id: string;
    type?: PublicKeyCredentialType;
    transports?: string[];
  }>;
  userVerification?: UserVerificationRequirement;
  extensions?: AuthenticationExtensionsClientInputs;
  hints?: string[];
}

export interface RegistrationCredentialJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports?: string[];
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  authenticatorAttachment?: string;
}

export interface AuthenticationCredentialJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle?: string | null;
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  authenticatorAttachment?: string;
}

function serializeAttestation(
  credential: PublicKeyCredential
): RegistrationCredentialJSON {
  const response = credential.response as AuthenticatorAttestationResponse;
  const transports =
    typeof response.getTransports === 'function' ? response.getTransports() : undefined;
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      transports,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
  };
}

function serializeAssertion(
  credential: PublicKeyCredential
): AuthenticationCredentialJSON {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
  };
}

function withPrfEval(
  extensions: AuthenticationExtensionsClientInputs | undefined,
  enablePrf: boolean
): AuthenticationExtensionsClientInputs | undefined {
  if (!enablePrf) return extensions;
  // Copy salt into a dedicated ArrayBuffer — some browsers reject SharedArrayBuffer views.
  const salt = new Uint8Array(PRF_SALT.byteLength);
  salt.set(PRF_SALT);
  return {
    ...extensions,
    prf: {
      eval: {
        first: salt.buffer,
      },
    },
  } as AuthenticationExtensionsClientInputs;
}

/** Strip non-JSON values (e.g. PRF ArrayBuffers) before posting to the API. */
export function sanitizeCredentialJson<T extends { clientExtensionResults?: unknown }>(
  credential: T
): T {
  const extensions = credential.clientExtensionResults;
  if (!extensions || typeof extensions !== 'object') {
    return { ...credential, clientExtensionResults: {} };
  }
  const safe: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(extensions as Record<string, unknown>)) {
    if (key === 'prf') {
      // Server does not need PRF output; sending ArrayBuffers breaks JSON / parsers.
      safe.prf = { enabled: true };
      continue;
    }
    try {
      JSON.stringify(value);
      safe[key] = value;
    } catch {
      /* skip non-serializable extension */
    }
  }
  return { ...credential, clientExtensionResults: safe };
}

export function extractPrfOutput(
  extensionResults: AuthenticationExtensionsClientOutputs
): ArrayBuffer | null {
  const prf = (extensionResults as { prf?: { results?: { first?: ArrayBuffer } } }).prf;
  const first = prf?.results?.first;
  return first ?? null;
}

export function didPrfSucceed(
  extensionResults: AuthenticationExtensionsClientOutputs
): boolean {
  return extractPrfOutput(extensionResults) != null;
}

function mapWebAuthnError(err: unknown, fallback: string): Error {
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError') {
      return new Error('Face ID was cancelled or blocked. Tap to try again.');
    }
    if (err.name === 'InvalidStateError') {
      return new Error('This device already has Face ID set up for Balance.');
    }
    if (err.name === 'NotSupportedError') {
      return new Error('Face ID is not supported in this browser.');
    }
    if (err.name === 'SecurityError') {
      return new Error('Face ID could not run on this site. Check that you opened the installed app.');
    }
    return new Error(err.message || fallback);
  }
  if (err instanceof Error) return err;
  return new Error(fallback);
}

export async function enrollPlatformAuthenticator(
  optionsJson: PublicKeyCredentialCreationOptionsJSON,
  opts: { enablePrf?: boolean } = {}
): Promise<{ credential: RegistrationCredentialJSON; prfOutput: ArrayBuffer | null }> {
  const enablePrf = opts.enablePrf ?? true;
  const publicKey = decodeCredentialCreationOptions(optionsJson);
  publicKey.extensions = withPrfEval(publicKey.extensions, enablePrf);

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({
      publicKey,
    })) as PublicKeyCredential | null;
  } catch (err) {
    throw mapWebAuthnError(err, 'Authenticator enrollment failed');
  }

  if (!credential) {
    throw new Error('Authenticator enrollment was cancelled');
  }

  const json = sanitizeCredentialJson(serializeAttestation(credential));
  const prfOutput = extractPrfOutput(credential.getClientExtensionResults());
  return { credential: json, prfOutput };
}

export async function assertPlatformAuthenticator(
  optionsJson: PublicKeyCredentialRequestOptionsJSON,
  opts: { enablePrf?: boolean; credentialIdFallback?: string } = {}
): Promise<{ credential: AuthenticationCredentialJSON; prfOutput: ArrayBuffer | null }> {
  const enablePrf = opts.enablePrf ?? false;
  const publicKey = decodeCredentialRequestOptions(optionsJson);
  publicKey.extensions = withPrfEval(publicKey.extensions, enablePrf);

  // Never call get() with an empty allowCredentials — that triggers Passkey UI on iOS.
  if (!publicKey.allowCredentials?.length && opts.credentialIdFallback) {
    publicKey.allowCredentials = [
      {
        id: base64UrlToBuffer(opts.credentialIdFallback),
        type: 'public-key',
        transports: ['internal'],
      },
    ];
  }
  if (!publicKey.allowCredentials?.length) {
    throw new Error('No Face ID credential is available on this device. Sign in with Google and enable Face ID again.');
  }
  publicKey.allowCredentials = ensureInternalTransports(publicKey.allowCredentials);

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.get({
      publicKey,
    })) as PublicKeyCredential | null;
  } catch (err) {
    throw mapWebAuthnError(err, 'Face ID authentication failed');
  }

  if (!credential) {
    throw new Error('Authentication was cancelled');
  }

  const json = sanitizeCredentialJson(serializeAssertion(credential));
  const prfOutput = extractPrfOutput(credential.getClientExtensionResults());
  return { credential: json, prfOutput };
}

/**
 * Offline local unlock: random challenge + allowCredentials + optional PRF.
 * Server is not involved; used only to obtain user verification / PRF secret.
 */
export async function assertLocalUnlock(opts: {
  credentialId: string;
  rpId?: string;
  enablePrf: boolean;
}): Promise<{ credentialId: string; prfOutput: ArrayBuffer | null }> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const publicKey = {
    challenge: challenge.buffer.slice(
      challenge.byteOffset,
      challenge.byteOffset + challenge.byteLength
    ),
    timeout: 60_000,
    rpId: opts.rpId ?? window.location.hostname,
    userVerification: 'required' as UserVerificationRequirement,
    allowCredentials: [
      {
        id: base64UrlToBuffer(opts.credentialId),
        type: 'public-key' as PublicKeyCredentialType,
        transports: ['internal'] as AuthenticatorTransport[],
      },
    ],
    extensions: withPrfEval(undefined, opts.enablePrf),
  } as PublicKeyCredentialRequestOptions & { hints?: string[] };
  publicKey.hints = ['client-device'];

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.get({
      publicKey,
    })) as PublicKeyCredential | null;
  } catch (err) {
    throw mapWebAuthnError(err, 'Face ID authentication failed');
  }

  if (!credential) {
    throw new Error('Authentication was cancelled');
  }

  return {
    credentialId: credential.id,
    prfOutput: extractPrfOutput(credential.getClientExtensionResults()),
  };
}
